import React, { useContext } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
    Drawer,
    Button,
    IconButton,
    Tooltip,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    Box,
} from '@mui/material';
import {
    Apps,
    ChevronLeft,
    Close,
    Dashboard,
    BarChart,
    Menu as MenuIcon,
    People,
    ShoppingBag,
    Diversity3,
    Assessment,
    Description,
    LocalShipping,
    Settings,
    SupportAgent,
    TrackChanges,
    LocalPrintshop,
    History,
    AccountTree,
    CalendarMonth,
} from '@mui/icons-material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { AuthContext } from '../contexts/AuthContext';
import { useTheme, alpha } from '@mui/material/styles';
import { BRAND } from '../theme';

export const SIDEBAR_DRAWER_WIDTH = 236;
export const SIDEBAR_COLLAPSED_DRAWER_WIDTH = 68;
const mobileDrawerWidth = 'min(86vw, 292px)';

const OtsWordIcon = ({ active, compact = false }) => (
    <Box
        sx={{
            width: 24,
            height: 24,
            borderRadius: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1.5px solid',
            borderColor: active ? alpha(BRAND.primary, 0.5) : '#9ca3af',
            backgroundColor: compact
                ? (active ? alpha(BRAND.primary, 0.1) : '#ffffff')
                : (active ? alpha(BRAND.primary, 0.08) : 'transparent'),
            color: compact ? (active ? BRAND.primaryDark : '#4b5563') : (active ? BRAND.primaryDark : '#4b5563'),
            fontWeight: 800,
            lineHeight: 1,
        }}
    >
        <Typography component="span" sx={{ fontSize: 9, fontWeight: 800 }}>
            OTS
        </Typography>
    </Box>
);

const XmlWordIcon = ({ active }) => (
    <Box
        sx={{
            width: 24,
            height: 24,
            borderRadius: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1.5px solid',
            borderColor: active ? alpha(BRAND.primary, 0.5) : '#9ca3af',
            backgroundColor: active ? alpha(BRAND.primary, 0.08) : 'transparent',
            color: active ? BRAND.primaryDark : '#4b5563',
            fontWeight: 800,
            lineHeight: 1,
        }}
    >
        <Typography component="span" sx={{ fontSize: 8.5, fontWeight: 800 }}>
            XML
        </Typography>
    </Box>
);

const Sidebar = ({
    mobileOpen,
    handleDrawerToggle,
    isCollapsed = false,
    onToggleCollapse,
    shouldAutoHideSidebar = false,
}) => {
    const { permissions, user } = useContext(AuthContext); // Get permissions from AuthContext
    const location = useLocation(); // Get current path
    const theme = useTheme(); // Access the theme
    const desktopDrawerWidth = isCollapsed ? SIDEBAR_COLLAPSED_DRAWER_WIDTH : SIDEBAR_DRAWER_WIDTH;

    const hasPermission = (permissionName) => {
        return permissions && permissions.includes(permissionName); // Ensure permissions is an array
    };
    const hasRole = (roleIds) => user && roleIds.includes(Number(user.RoleId));
    const getIsSelected = (link) => location.pathname === link;
    const closeTemporaryDrawer = () => {
        if (mobileOpen) {
            handleDrawerToggle?.();
        }
    };

    const menuSections = [
        {
            label: 'Operations Control',
            icon: <Apps />,
            items: [
                {
                    label: 'Dashboard',
                    icon: <Dashboard />,
                    link: '/dashboard',
                },
                {
                    label: 'IV KPI Dashboard',
                    icon: <BarChart />,
                    link: '/kpi-generic',
                },
                {
                    label: 'FP KPI Dashboard',
                    icon: <BarChart />,
                    link: '/funky-pigeon-kpi',
                },
            ],
        },
        {
            label: 'Fulfilment',
            icon: <LocalShipping />,
            items: [
                {
                    label: 'Client Orders',
                    icon: <ShoppingBag />,
                    link: '/orders',
                },
                {
                    label: 'Shipping Station',
                    icon: <LocalShipping />,
                    link: '/linx-dispatch',
                },
                hasRole([1, 3]) && {
                    label: 'Order Tracker',
                    icon: <TrackChanges />,
                    link: '/order-tracker',
                },
                {
                    label: 'Manage Batches',
                    icon: <FactCheckIcon />,
                    link: '/manage-batches',
                },
                hasPermission('PrintProductionJobs') && {
                    label: 'Batch Print',
                    icon: <LocalPrintshop />,
                    link: '/batch-print',
                },
                hasPermission('ViewPrintBatchAudit') && {
                    label: 'Batch Print Audit',
                    icon: <History />,
                    link: '/print-batch-audit',
                },
                {
                    label: 'Branston Converter',
                    icon: <XmlWordIcon active={location.pathname === '/branston-converter'} />,
                    link: '/branston-converter',
                },
            ],
        },
        {
            label: 'Reporting',
            icon: <Assessment />,
            items: [
                hasPermission('ViewReports') && {
                    label: 'Reports',
                    icon: <Assessment />,
                    link: '/reports',
                },
                hasPermission('ViewReports') && {
                    label: 'Operations Reporting',
                    icon: <Assessment />,
                    link: '/operational-reporting',
                },
                hasPermission('ViewReports') && {
                    label: 'OTS DOT Report',
                    icon: <OtsWordIcon active={location.pathname === '/ots-dot-report'} compact={isCollapsed} />,
                    link: '/ots-dot-report',
                },
                hasPermission('ViewReports') && {
                    label: 'Monthly Dispatch Tracker',
                    icon: <CalendarMonth />,
                    link: '/monthly-dispatch-tracker',
                },
            ],
        },
        {
            label: 'System Team',
            icon: <Settings />,
            items: [
                hasPermission('ManageClients') && {
                    label: 'Clients Management',
                    icon: <Diversity3 />,
                    link: '/clients',
                },
                hasPermission('ManageUsers') && {
                    label: 'Users Management',
                    icon: <People />,
                    link: '/user-management',
                },
                hasPermission('ViewLogs') && {
                    label: 'Logs',
                    icon: <Description />,
                    link: '/logs',
                },
                hasPermission('ManagePrintWorkflowConfig') && {
                    label: 'Print Workflow Settings',
                    icon: <AccountTree />,
                    link: '/print-workflow-settings',
                },
            ],
        },
    ]
        .map((section) => ({
            ...section,
            items: section.items.filter(Boolean),
        }))
        .filter((section) => section.items.length > 0);

    const renderMenuItems = (items, compact) => {
        return items.map((item, index) => {
            const selected = getIsSelected(item.link);
            const listItemContent = (
                <ListItemButton
                    key={index}
                    component={RouterLink}
                    to={item.link}
                    selected={selected}
                    onClick={closeTemporaryDrawer}
                    sx={{
                        minHeight: 38,
                        justifyContent: compact ? 'center' : 'flex-start',
                        mx: compact ? 0.85 : 1,
                        mb: 0.15,
                        px: compact ? 0.85 : 1,
                        py: 0.45,
                        borderRadius: 1.5,
                        color: selected ? BRAND.dark : alpha(BRAND.dark, 0.68),
                        backgroundColor: selected ? alpha(BRAND.primary, 0.08) : 'transparent',
                        border: '1px solid',
                        borderColor: selected ? alpha(BRAND.primary, 0.18) : 'transparent',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'background-color 160ms ease, border-color 160ms ease, color 160ms ease, transform 160ms ease',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 6,
                            top: 8,
                            bottom: 8,
                            width: 3,
                            borderRadius: 99,
                            backgroundColor: selected ? BRAND.primary : 'transparent',
                            transition: 'background-color 160ms ease',
                        },
                        '&:hover': {
                            backgroundColor: selected
                                ? alpha(BRAND.primary, 0.1)
                                : alpha(BRAND.secondary, 0.055),
                            color: selected ? BRAND.dark : theme.palette.text.primary,
                            transform: compact ? 'none' : 'translateX(2px)',
                        },
                        '&.Mui-selected': {
                            backgroundColor: alpha(BRAND.primary, 0.08),
                            '&:hover': {
                                backgroundColor: alpha(BRAND.primary, 0.1),
                            },
                        },
                        '&.Mui-focusVisible': {
                            outline: `2px solid ${alpha(BRAND.secondary, 0.45)}`,
                            outlineOffset: 2,
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            width: 28,
                            height: 28,
                            minWidth: 28,
                            mr: compact ? 0 : 0.85,
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderRadius: 1.5,
                            color: selected ? BRAND.primaryDark : alpha(BRAND.dark, 0.52),
                            backgroundColor: selected ? alpha(BRAND.primary, 0.1) : alpha(BRAND.dark, 0.035),
                            transition: 'color 160ms ease, background-color 160ms ease',
                            '& svg': {
                                fontSize: 18.5,
                            },
                        }}
                    >
                        {item.icon}
                    </ListItemIcon>
                    {!compact && (
                        <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                                fontSize: 13,
                                lineHeight: 1.2,
                                fontWeight: selected ? 800 : 600,
                                color: selected ? BRAND.dark : alpha(BRAND.dark, 0.72),
                                noWrap: true,
                            }}
                        />
                    )}
                </ListItemButton>
            );

            return compact ? (
                <Tooltip title={item.label} placement="right" key={index}>
                    {listItemContent}
                </Tooltip>
            ) : listItemContent;
        });
    };

    const renderSections = (compact) => (
        <List sx={{ px: 0, py: compact ? 0.5 : 0.35 }}>
            {menuSections.map((section) => {
                const sectionActive = section.items.some((item) => getIsSelected(item.link));

                return (
                    <Box key={section.label} sx={{ mb: compact ? 0.45 : 0.75 }}>
                        {compact ? (
                            <Tooltip title={section.label} placement="right">
                                <Box
                                    sx={{
                                        mx: 1,
                                        mb: 0.2,
                                        height: 24,
                                        borderRadius: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: sectionActive ? BRAND.primaryDark : '#8b98a7',
                                        backgroundColor: sectionActive
                                            ? alpha(BRAND.primary, 0.08)
                                            : 'transparent',
                                    }}
                                >
                                    {React.cloneElement(section.icon, { sx: { fontSize: 17 } })}
                                </Box>
                            </Tooltip>
                        ) : (
                            <ListItem disablePadding sx={{ px: 1.4, pb: 0.25, pt: 0.35 }}>
                                <ListItemText
                                    primary={section.label}
                                    primaryTypographyProps={{
                                        fontSize: 10.5,
                                        fontWeight: 900,
                                        letterSpacing: 0.65,
                                        lineHeight: 1.1,
                                        color: '#7b899a',
                                        textTransform: 'uppercase',
                                    }}
                                />
                            </ListItem>
                        )}
                        {renderMenuItems(section.items, compact)}
                    </Box>
                );
            })}
        </List>
    );

    const renderDrawerContent = (isTemporary = false) => {
        const compact = isCollapsed && !isTemporary;

        return (
        <>
            <Box
                sx={{
                    px: compact ? 1 : 1.5,
                    pt: compact ? 0.9 : 1,
                    pb: compact ? 0.9 : 0.85,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: compact ? 'center' : 'space-between',
                    gap: 1,
                }}
            >
                {!compact && (
                    <Box
                        component={RouterLink}
                        to="/dashboard"
                        onClick={closeTemporaryDrawer}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0,
                            textDecoration: 'none',
                        }}
                    >
                        <Box
                            component="img"
                            src="/images/logo.png"
                            alt="Logo"
                            sx={{
                                width: 120,
                                maxHeight: 40,
                                objectFit: 'contain',
                            }}
                        />
                    </Box>
                )}
                {compact && (
                    <IconButton
                        size="small"
                        aria-label="Expand menu"
                        onClick={() => onToggleCollapse?.()}
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            color: '#ffffff',
                            backgroundColor: BRAND.dark,
                            boxShadow: `0 10px 20px ${alpha(BRAND.dark, 0.22)}`,
                            '&:hover': {
                                backgroundColor: BRAND.secondary,
                            },
                        }}
                    >
                        <MenuIcon fontSize="small" />
                    </IconButton>
                )}
                {isTemporary ? (
                    <IconButton
                        size="small"
                        aria-label="Close menu"
                        onClick={handleDrawerToggle}
                        sx={{
                            display: { xs: 'inline-flex', sm: shouldAutoHideSidebar ? 'inline-flex' : 'none' },
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            color: '#344256',
                            border: `1px solid ${alpha(BRAND.secondary, 0.16)}`,
                            backgroundColor: alpha(BRAND.secondary, 0.055),
                            '&:hover': {
                                backgroundColor: alpha(BRAND.secondary, 0.1),
                            },
                        }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                ) : !compact ? (
                    <Tooltip title={compact ? 'Expand menu' : 'Minimize menu'}>
                    <IconButton
                        size="small"
                        onClick={() => onToggleCollapse?.()}
                        sx={{
                            display: { xs: 'none', sm: 'inline-flex' },
                                ml: compact ? 0 : 0.5,
                                width: 32,
                                height: 32,
                                borderRadius: 1.75,
                                color: '#344256',
                            border: '1px solid',
                                borderColor: alpha(BRAND.secondary, 0.14),
                                backgroundColor: alpha(BRAND.secondary, 0.045),
                            '&:hover': {
                                backgroundColor: alpha(BRAND.secondary, 0.1),
                            },
                        }}
                    >
                            <ChevronLeft fontSize="small" />
                    </IconButton>
                </Tooltip>
                ) : null}
            </Box>
                <Divider sx={{ mx: 1.25, borderColor: alpha(BRAND.dark, 0.08) }} />
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        py: 0.65,
                        '&::-webkit-scrollbar': {
                            width: 6,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            borderRadius: 99,
                            backgroundColor: alpha(BRAND.secondary, 0.18),
                        },
                    }}
                >
                    {renderSections(compact)}
                </Box>
                <Divider sx={{ mx: 1.25, borderColor: alpha(BRAND.dark, 0.08) }} />
            {!compact && (
                <Box
                    sx={{
                            p: 1,
                    }}
                >
                    <Button
                        component="a"
                        href="https://dpsdigital.sharepoint.com/sites/DPSHelpdesk/Lists/Tickets/AllItems.aspx"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="contained"
                            fullWidth
                            startIcon={<SupportAgent />}
                        sx={{
                            textDecoration: 'none',
                            minHeight: 36,
                            py: 0.7,
                            borderRadius: 1.5,
                            fontSize: 13,
                            backgroundColor: BRAND.dark,
                            color: '#ffffff',
                            boxShadow: `0 2px 8px ${alpha(BRAND.dark, 0.18)}`,
                            '& .MuiButton-startIcon, & .MuiButton-startIcon svg': {
                                color: '#ffffff',
                            },
                            '&:hover': {
                                backgroundColor: BRAND.secondaryDark,
                                color: '#ffffff',
                                boxShadow: `0 4px 12px ${alpha(BRAND.secondaryDark, 0.2)}`,
                            },
                            '&:focus-visible': {
                                outline: `3px solid ${alpha(BRAND.secondary, 0.24)}`,
                                outlineOffset: 2,
                            },
                        }}
                    >
                        Support
                    </Button>
                </Box>
            )}
        </>
        );
    };

    return (
        <>
            <Drawer
                sx={{
                    display: shouldAutoHideSidebar ? 'block' : { xs: 'block', sm: 'none' },
                    zIndex: 1300,
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: mobileDrawerWidth,
                        minHeight: '100dvh',
                        background: `linear-gradient(180deg, #ffffff 0%, ${BRAND.surface} 46%, ${BRAND.surfaceAlt} 100%)`,
                        borderRight: `1px solid ${alpha(BRAND.dark, 0.08)}`,
                        boxShadow: '18px 0 46px rgba(15, 23, 42, 0.18)',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
                variant="temporary"
                anchor="left"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true,
                }}
            >
                {renderDrawerContent(true)}
            </Drawer>

            <Drawer
                sx={{
                    display: shouldAutoHideSidebar ? 'none' : { xs: 'none', sm: 'block' },
                    width: desktopDrawerWidth,
                    flexShrink: 0,
                    zIndex: 1300,
                    '& .MuiDrawer-paper': {
                        width: desktopDrawerWidth,
                        boxSizing: 'border-box',
                        overflowX: 'hidden',
                        background: `linear-gradient(180deg, #ffffff 0%, ${BRAND.surface} 48%, ${BRAND.surfaceAlt} 100%)`,
                        borderRight: `1px solid ${alpha(BRAND.dark, 0.08)}`,
                        boxShadow: '10px 0 30px rgba(15, 23, 42, 0.055)',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.shorter,
                        }),
                    },
                }}
                variant="permanent"
                anchor="left"
            >
                {renderDrawerContent(false)}
            </Drawer>
        </>
    );
};

export default Sidebar;
